import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { KanbanSquare, Loader2, Plus } from 'lucide-react'

import {
  listProductionAssignments,
  type ProductionAssignment,
  type ProductionDepartment,
  type ProductionMember,
} from '@/api/studio-production'
import { ProductionAssignmentsTable } from '@/features/studio/components/ProductionAssignmentsTable'
import { useAssignmentItemLabels } from '@/features/studio/hooks/useAssignmentItemLabels'
import { BulkAssignWorkModal } from '@/features/studio/modals/BulkAssignWorkModal'
import { ProtoBtn, ProtoPagination } from '@/features/studio/components/ui-kit'

const PAGE_SIZE = 20

export function ProductionAssignmentsPanel({
  productionId,
  members,
  departments,
  tasksPath,
  gate,
  onChanged,
}: {
  productionId: string
  members: ProductionMember[]
  departments: ProductionDepartment[]
  tasksPath: string
  gate: { isOwner: boolean; isDeptAdmin: boolean; myDepartmentId: string | null }
  onChanged?: () => void
}) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [assignments, setAssignments] = useState<ProductionAssignment[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(0)
  const [loading, setLoading] = useState(true)
  const [bulkOpen, setBulkOpen] = useState(false)
  const { resolve, loading: labelsLoading } = useAssignmentItemLabels(productionId)

  const reload = useCallback(async () => {
    if (!productionId) return
    setLoading(true)
    try {
      const res = await listProductionAssignments(productionId, undefined, {
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      })
      setAssignments(res.items)
      setTotal(res.total)
    } catch {
      setAssignments([])
      setTotal(0)
    } finally {
      setLoading(false)
    }
  }, [productionId, page])

  useEffect(() => {
    void reload()
  }, [reload])

  const tableLoading = loading || labelsLoading
  const canAssign = gate.isOwner || gate.isDeptAdmin

  return (
    <>
      <div className="mb-3 flex flex-wrap items-center justify-end gap-2">
        {canAssign ? (
          <ProtoBtn size="sm" onClick={() => setBulkOpen(true)}>
            <Plus className="h-3.5 w-3.5" />
            {t('bulkAssign.open')}
          </ProtoBtn>
        ) : null}
        <ProtoBtn size="sm" variant="secondary" onClick={() => navigate(tasksPath)}>
          <KanbanSquare className="h-3.5 w-3.5" />
          {t('team.assignmentsOpenKanban')}
        </ProtoBtn>
      </div>

      {tableLoading ? (
        <div className="flex h-40 items-center justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-sky-400" />
        </div>
      ) : (
        <>
          <ProductionAssignmentsTable
            assignments={assignments}
            members={members}
            emptyMessage={t('team.assignmentsEmpty')}
            resolveScopeLabel={resolve}
          />
          {total > 0 ? (
            <ProtoPagination
              page={page}
              pageSize={PAGE_SIZE}
              total={total}
              onPageChange={setPage}
              prevLabel={t('common.prevPage')}
              nextLabel={t('common.nextPage')}
            />
          ) : null}
        </>
      )}

      <BulkAssignWorkModal
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        productionId={productionId}
        members={members}
        departments={departments}
        gate={gate}
        onAssigned={() => {
          void reload()
          onChanged?.()
        }}
      />
    </>
  )
}
